import React, { forwardRef, type ReactNode, useState } from "react";
import { type Size } from "@purpur/action";
import { Button, BUTTON_VARIANT } from "@purpur/button";
import type { BaseProps } from "@purpur/common-types";
import { IconInfo } from "@purpur/icon/info";
import { Paragraph, ParagraphVariant } from "@purpur/paragraph";
import { useThemePortalContainer } from "@purpur/theme";
import { purpurMotionDuration400 } from "@purpur/tokens";
import * as RadixTooltip from "@radix-ui/react-tooltip";
import c from "classnames/bind";

import styles from "./tooltip.module.scss";

const cx = c.bind(styles);

export const TOOLTIP_POSITION = {
  TOP: "top",
  BOTTOM: "bottom",
  LEFT: "left",
  RIGHT: "right",
} as const;

export type TooltipPosition = (typeof TOOLTIP_POSITION)[keyof typeof TOOLTIP_POSITION];

export const TOOLTIP_ALIGN = {
  START: "start",
  CENTER: "center",
  END: "end",
} as const;
export type TooltipAlign = (typeof TOOLTIP_ALIGN)[keyof typeof TOOLTIP_ALIGN];

type WithTriggerElement = {
  triggerElement: ReactNode;
  triggerAriaLabel?: never;
};

type WithoutTriggeElement = {
  triggerElement?: never;
  triggerAriaLabel: string;
};

export type TooltipProps = Omit<BaseProps, "children"> & {
  align?: TooltipAlign;
  buttonSize?: Size;
  children: ReactNode;
  /**
   * Optional DOM element to portal the tooltip content into. When omitted,
   * the tooltip renders inside the nearest `ThemeProvider` so it inherits
   * the active color scheme. Pass `document.body` (or any other element) to
   * override. Pass `null` to fall back to Radix' default (`document.body`).
   */
  container?: HTMLElement | null;
  negative?: boolean;
  position?: TooltipPosition;
  triggerAriaLabel?: string;
  triggerElement?: ReactNode;
  contentClassName?: string;
} & (WithTriggerElement | WithoutTriggeElement);

const rootClassName = "purpur-tooltip";

export const Tooltip = forwardRef<HTMLButtonElement, TooltipProps>(
  (
    {
      ["data-testid"]: dataTestId,
      buttonSize = "md",
      children,
      className,
      container,
      contentClassName,
      negative = false,
      position = TOOLTIP_POSITION.TOP,
      align = TOOLTIP_ALIGN.CENTER,
      triggerAriaLabel,
      triggerElement,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const themePortalContainer = useThemePortalContainer();
    const portalContainer =
      container === undefined ? themePortalContainer : container ?? undefined;
    const classes = cx(
      className,
      rootClassName,
      `${rootClassName}--primary${negative ? "-negative" : ""}`
    );
    const tooltipButton = (
      <Button
        ref={ref}
        aria-label={triggerAriaLabel ?? ""}
        aria-expanded={isOpen}
        variant={BUTTON_VARIANT.TERTIARY_PURPLE}
        negative={negative}
        iconOnly
        data-testid={`${dataTestId}-trigger-button`}
        size={buttonSize}
      >
        <IconInfo size="md" />
      </Button>
    );

    return (
      <div data-testid={dataTestId} className={classes}>
        <RadixTooltip.Provider delayDuration={parseInt(purpurMotionDuration400)}>
          <RadixTooltip.Root open={isOpen} onOpenChange={setIsOpen} {...props}>
            <RadixTooltip.Trigger
              asChild
              onClick={(e) => {
                e.preventDefault();
                setIsOpen(true);
              }}
            >
              {triggerElement ?? tooltipButton}
            </RadixTooltip.Trigger>
            <RadixTooltip.Portal container={portalContainer}>
              <RadixTooltip.Content
                side={position}
                align={align}
                className={cx(
                  contentClassName,
                  `${rootClassName}__content`,
                  `${rootClassName}__content--primary${negative ? "-negative" : ""}`
                )}
                sideOffset={-5}
                data-testid={`${dataTestId}-content`}
              >
                {typeof children === "string" ? (
                  <Paragraph
                    variant={ParagraphVariant.PARAGRAPH100}
                    negative={!negative}
                    data-testid={`${dataTestId}-paragraph`}
                  >
                    {children}
                  </Paragraph>
                ) : (
                  children
                )}
                <RadixTooltip.Arrow
                  className={cx(
                    `${rootClassName}__arrow`,
                    `${rootClassName}__arrow--primary${negative ? "-negative" : ""}`
                  )}
                />
              </RadixTooltip.Content>
            </RadixTooltip.Portal>
          </RadixTooltip.Root>
        </RadixTooltip.Provider>
      </div>
    );
  }
);

Tooltip.displayName = "Tooltip";
